import React, { useEffect, useRef, useState } from 'react';
import { Users } from 'lucide-react';
import { PropRow } from './ui/PropRow';

export interface AttendeeEntry {
  email: string;
  name?: string;
}

export interface AttendeeUserOption {
  id: string;
  email: string | null;
  full_name?: string | null;
}

export interface AttendeesPickerProps {
  attendees: AttendeeEntry[];
  onAttendeesChange(attendees: AttendeeEntry[]): void;
  /** Utenti CRM proposti nell'autocomplete (colleghi). */
  users: AttendeeUserOption[];
  /** Contatti del cliente/lead, mostrati come chip suggerite cliccabili. */
  availableContacts: AttendeeEntry[];
}

/**
 * Invitati come riga di proprietà (redesign T6, mockup vista 2): PropRow "Invitati (N)"
 * con le chip blu degli invitati correnti, seguita da una riga ghost "+ Aggiungi invitato"
 * che rivela l'input di ricerca (autocomplete colleghi CRM + hint email libera) e mostra
 * accanto i contatti del cliente/lead non ancora invitati come chip grigie "Suggerito:".
 *
 * Logica INVARIATA rispetto alla versione precedente (fonte: LeadStatusUpdateDrawer.tsx
 * :2114-2255, identico in StatusUpdateDrawer.tsx): pre-selezione contatti cliente, aggiunta
 * email libere con Invio, hint 'Premi + o Invio per aggiungere "…"', rimozione — cambia
 * solo il render (niente più bottone "+" blu pieno, niente card wrapper).
 */
export function AttendeesPicker({ attendees, onAttendeesChange, users, availableContacts }: AttendeesPickerProps) {
  const [search, setSearch] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showInput, setShowInput] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (showInput) {
      inputRef.current?.focus();
    }
  }, [showInput]);

  const addAttendee = (entry: AttendeeEntry) => {
    if (attendees.some((a) => a.email === entry.email)) return;
    onAttendeesChange([...attendees, entry]);
  };

  const removeAttendee = (email: string) => {
    onAttendeesChange(attendees.filter((a) => a.email !== email));
  };

  const addFromSearch = () => {
    const email = search.trim();
    if (email && email.includes('@') && !attendees.some((a) => a.email === email)) {
      addAttendee({ email });
      setSearch('');
    }
  };

  const suggestions = users.filter(
    (u) =>
      u.email &&
      !attendees.some((a) => a.email === u.email) &&
      (u.full_name?.toLowerCase().includes(search.toLowerCase()) ||
        u.email?.toLowerCase().includes(search.toLowerCase()))
  );

  const suggestedContacts = availableContacts.filter(
    (contact) => !attendees.some((a) => a.email === contact.email)
  );

  return (
    <div className="flex flex-col">
      <PropRow
        icon={<Users className="h-3 w-3" strokeWidth={1.5} />}
        label={`Invitati (${attendees.length})`}
        wrap
      >
        {attendees.length === 0 ? (
          <span className="text-[#787774] dark:text-gray-400">Nessun invitato</span>
        ) : (
          attendees.map((attendee) => (
            <span
              key={attendee.email}
              className="inline-flex items-center gap-1 text-[11px] px-2.5 py-0.5 rounded-full bg-[rgba(35,131,226,0.12)] text-[#2383E2] dark:text-[#5CA5E8] whitespace-nowrap"
            >
              <span className="truncate max-w-[140px]">{attendee.name || attendee.email}</span>
              <button
                type="button"
                onClick={() => removeAttendee(attendee.email)}
                className="hover:opacity-70 font-medium"
              >
                ×
              </button>
            </span>
          ))
        )}
      </PropRow>

      <PropRow icon={null} label="" wrap>
        {!showInput && (
          <button
            type="button"
            onClick={() => setShowInput(true)}
            className="text-xs text-[#787774] dark:text-gray-400 hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/[0.06] rounded px-2 py-1.5"
          >
            + Aggiungi invitato
          </button>
        )}

        {showInput && (
          <div className="relative min-w-[220px]">
            <input
              ref={inputRef}
              type="text"
              autoComplete="off"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setShowSuggestions(true);
              }}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              placeholder="Digita email da invitare o il nome di un collega..."
              className="w-full px-2 py-1 text-[13px] bg-white dark:bg-[#373C3F] border border-[rgba(55,53,47,0.16)] dark:border-[#4A5053] rounded text-[#37352F] dark:text-white placeholder:text-[#787774] dark:placeholder:text-gray-500 focus:outline-none focus:border-[#2383E2] focus:ring-2 focus:ring-[#2383E2]/20"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addFromSearch();
                }
              }}
            />

            {showSuggestions && search.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white dark:bg-[#373C3F] border border-[rgba(55,53,47,0.09)] dark:border-[#3F4447] rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {suggestions.slice(0, 5).map((user) => (
                  <button
                    key={user.id}
                    type="button"
                    onClick={() => {
                      addAttendee({ email: user.email as string, name: user.full_name || undefined });
                      setSearch('');
                      setShowSuggestions(false);
                    }}
                    className="w-full px-3 py-2 text-left text-sm text-[#37352F] dark:text-white hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.04] flex items-center gap-2"
                  >
                    <div className="w-6 h-6 rounded-full bg-[#2383E2]/10 flex items-center justify-center text-xs font-medium text-[#2383E2]">
                      {(user.full_name || user.email || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{user.full_name || user.email}</div>
                      {user.full_name && (
                        <div className="text-xs text-[#787774] dark:text-gray-400 truncate">{user.email}</div>
                      )}
                    </div>
                  </button>
                ))}
                {suggestions.length === 0 && search.includes('@') && (
                  <div className="px-3 py-2 text-sm text-[#787774] dark:text-gray-400">
                    Premi + o Invio per aggiungere "{search}"
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {suggestedContacts.length > 0 && (
          <>
            <span className="text-[11px] text-[#787774] dark:text-gray-400 ml-1">Suggerito:</span>
            {suggestedContacts.map((contact) => (
              <button
                key={contact.email}
                type="button"
                onClick={() => addAttendee(contact)}
                className="inline-flex items-center gap-1 text-[11px] px-2.5 py-0.5 rounded-full bg-[rgba(55,53,47,0.08)] dark:bg-white/[0.08] text-[#37352F] dark:text-white hover:bg-[rgba(55,53,47,0.12)] dark:hover:bg-white/[0.12] whitespace-nowrap"
              >
                {contact.name || contact.email}
              </button>
            ))}
          </>
        )}
      </PropRow>
    </div>
  );
}
